import { FC, useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { Modal, Text, Chip } from '@frontend/design-system';
import { chipsContainerStyle, activeLocationChipStyle } from './payments-in-forms-onboarding-modal.styles';
import { PaymentsInFormsOnboardingModalProps } from './payments-in-forms-onboarding-modal.type';

export const PaymentsInFormsOnboardingModal: FC<PaymentsInFormsOnboardingModalProps> = (props) => {
  const {
    modalControls,
    formsEnabledLocationIds,
    paymentsEnabledLocationIdsMap,
    canUsePaymentsField,
    trakingIds,
    onContinueWithoutOnboardingAllLocations,
    goBackBtnLabel,
    onGoBack,
  } = props;
  const { t } = useTranslation('forms');
  const { getScopeName } = useAppScopeStore();
  const [paymentsEnabledFormsLocationIds, setPaymentsEnabledFormsLocationIds] = useState<string[]>([]);
  const { navigate, isOpen } = useSettingsNavigate();
  const openedSettingsRef = useRef(false);
  const { closeModal } = modalControls;

  useEffect(() => {
    if (formsEnabledLocationIds.length === 0) {
      return;
    }

    const _paymentsEnabledFormsLocationIds = formsEnabledLocationIds.filter((locationId) => {
      return paymentsEnabledLocationIdsMap[locationId];
    });
    setPaymentsEnabledFormsLocationIds(_paymentsEnabledFormsLocationIds);
  }, [paymentsEnabledLocationIdsMap, formsEnabledLocationIds]);

  useEffect(() => {
    if (!openedSettingsRef.current) {
      return;
    }

    if (!isOpen) {
      openedSettingsRef.current = false;
      // TODO: Refetch the payments enabled locations mapping
    }
  }, [isOpen]);

  const navigateToPaymentsSettings = useCallback(() => {
    closeModal();
    openedSettingsRef.current = true;
    navigate({ to: '/payments/account-info' });
  }, [closeModal, navigate]);

  function getHeaderContent(): string {
    if (!canUsePaymentsField) {
      if (props.usageContext === 'form-builder' && props.sharedLocationIds.length > 0) {
        return props.sharedLocationIds.length > 1
          ? t('Payments are not set up for all shared locations for this form')
          : t('Payments are not set up for the shared location for this form');
      }

      return formsEnabledLocationIds.length === 1
        ? t('Payments are not set up for this location')
        : t('Payments are not set up for any of the locations');
    }

    return t('Only {{count}} locations have payments set up', { count: paymentsEnabledFormsLocationIds.length });
  }

  function getBodyContent() {
    let locationIds = formsEnabledLocationIds;
    let text = t('Not all of your locations have payments set up.');

    if (!canUsePaymentsField) {
      switch (props.usageContext) {
        case 'form-builder':
          if (props.sharedLocationIds.length > 0) {
            locationIds = props.sharedLocationIds;
            text =
              props.sharedLocationIds.length > 1
                ? t('You cannot add payment fields until all the following shared locations have payments set up:')
                : t('You cannot add payment fields until you set up payments for the shared location.');
          } else {
            text =
              formsEnabledLocationIds.length > 1
                ? t('You cannot add payment fields until you set up payments for at least one location.')
                : t('You cannot add payment fields until you set up payments for this location.');
          }
          break;

        case 'form-templates':
          text =
            formsEnabledLocationIds.length > 1
              ? t('You cannot use the template until you set up payments for at least one location.')
              : t('You cannot use the template until you set up payments for this location.');
      }
    }

    return (
      <>
        <Text>{text}</Text>
        <div css={chipsContainerStyle}>
          {locationIds.map((locationId) => {
            const isPaymentsEnabled = paymentsEnabledLocationIdsMap[locationId];
            return (
              <Chip.Location key={locationId} size='large' css={isPaymentsEnabled && activeLocationChipStyle}>
                {getScopeName(locationId)}
              </Chip.Location>
            );
          })}
        </div>
      </>
    );
  }

  function getFooterContent() {
    if (!canUsePaymentsField) {
      return (
        <Modal.Footer
          secondary={{
            label: t('Set up payments'),
            onClick: navigateToPaymentsSettings,
            trackingId: trakingIds.setUpPayments,
          }}
          tertiary={
            onGoBack
              ? {
                  label: goBackBtnLabel || t('Go back'),
                  iconName: 'caret-left-small',
                  onClick: onGoBack,
                  trackingId: trakingIds.goBack,
                }
              : undefined
          }
        />
      );
    }

    return (
      <Modal.Footer
        primary={{
          label: t('Continue'),
          onClick: onContinueWithoutOnboardingAllLocations ? onContinueWithoutOnboardingAllLocations : closeModal,
          trackingId: trakingIds.continueWithoutPaymentsOnboardingAllLocations,
        }}
        secondary={{
          label: t('Set up payments'),
          onClick: navigateToPaymentsSettings,
          trackingId: trakingIds.setUpPayments,
        }}
        tertiary={
          onGoBack
            ? {
                label: goBackBtnLabel || t('Go back'),
                iconName: 'caret-left-small',
                onClick: onGoBack,
                trackingId: trakingIds.goBack,
              }
            : undefined
        }
      />
    );
  }

  return (
    <Modal {...modalControls.modalProps} maxWidth={770}>
      <Modal.Header onClose={closeModal} closeBtnTrackingId={trakingIds.cancelPaymentsOnboarding}>
        {getHeaderContent()}
      </Modal.Header>
      <Modal.Body>{getBodyContent()}</Modal.Body>
      {getFooterContent()}
    </Modal>
  );
};
